import React, { useEffect, useRef } from 'react';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import { Spanish } from 'flatpickr/dist/l10n/es.js';
import InputIconByKey from './InputIconByKey';
import RequiredAsteriskTooltip from './RequiredAsteriskTooltip';
import { IconKey } from '../../../types/system/icons';

interface CompactDateFieldProps {
  /** Etiqueta opcional; si se omite, se muestra solo el input. */
  label?: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  iconKey?: IconKey;
  disabled?: boolean;
  required?: boolean;
  /** Aplica margen inferior (mb-4). Por defecto false para usarlo en filas/tablas compactas. */
  withMargin?: boolean;
  className?: string;
}

/**
 * Versión reducida de {@link DatePicker}: mismo calendario flatpickr, colores
 * y comportamiento (borde, foco con anillo primario, modo oscuro), pero con
 * altura y paddings pensados para filas de tabla, tarjetas de resumen y
 * celdas compactas donde el DatePicker grande desborda.
 */
const CompactDateField: React.FC<CompactDateFieldProps> = ({
  label,
  name,
  value,
  onChange,
  error,
  iconKey,
  disabled,
  required,
  withMargin = false,
  className = '',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const flatpickrInstance = useRef<flatpickr.Instance | null>(null);

  useEffect(() => {
    if (inputRef.current && !flatpickrInstance.current) {
      flatpickrInstance.current = flatpickr(inputRef.current, {
        locale: Spanish,
        dateFormat: 'Y-m-d',
        defaultDate: value,
        allowInput: true,
        disableMobile: true,
        static: false,
        appendTo: document.body,
        position: 'auto',
        clickOpens: !disabled,
        onChange: (selectedDates) => {
          if (selectedDates.length > 0) {
            onChange(selectedDates[0].toISOString().split('T')[0]);
          }
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, onChange, disabled]);

  useEffect(() => {
    if (flatpickrInstance.current) {
      flatpickrInstance.current.set('clickOpens', !disabled);
    }
  }, [disabled]);

  return (
    <div className={`${withMargin ? 'mb-4' : ''} ${className}`}>
      {label && (
        <label
          htmlFor={name}
          className="mb-1.5 flex items-center gap-1 text-xs font-medium text-black dark:text-white"
        >
          {label}
          {required && <RequiredAsteriskTooltip delay={400} />}
        </label>
      )}

      <div
        className={`
          bg-gray-1
          flex
          h-10
          items-stretch
          overflow-hidden
          rounded-lg
          border
          ${error ? 'border-danger' : 'border-stroke'}
          transition-all
          duration-200
          focus-within:border-primary
          focus-within:ring-2
          focus-within:ring-primary/10
          dark:border-strokedark
          dark:bg-meta-4
          ${disabled ? 'cursor-not-allowed opacity-70' : ''}
        `}
      >
        {iconKey && (
          <div className="flex items-center justify-center border-r border-stroke bg-gray-2 px-2.5 text-bodydark2 dark:border-strokedark dark:bg-boxdark-2">
            <InputIconByKey iconKey={iconKey} />
          </div>
        )}

        <input
          ref={inputRef}
          id={name}
          name={name}
          type="text"
          value={value ?? ''}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          autoComplete="off"
          data-class="flatpickr-right"
          className="w-full min-w-0 bg-transparent px-3 text-sm text-black outline-none dark:text-white"
        />
      </div>

      {error && <p className="mt-1 text-xs text-danger">{error}</p>}
    </div>
  );
};

export default CompactDateField;
